import { type ConditionDto, useCases, usePatients } from "@repo/data-store";
import { PropertyPicker, PropertyPickerCalendar } from "@repo/ui/components/property-picker";
import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { Label } from "@repo/ui/components/ui/label";
import { Textarea } from "@repo/ui/components/ui/textarea";
import { CalendarDays } from "lucide-react";
import { useCallback } from "react";
import {
  addCaseDiagnosisFromForm,
  type ConditionFormValues,
  conditionFormSchema,
  DEFAULT_CONDITION_FORM_VALUES,
  updateConditionFromForm,
  useAppForm,
} from "@/features/patients/forms/use-condition-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { useSubmitOnce } from "@/shared/forms/submit-once";

export interface AddConditionDialogProps {
  /** Pre-selected patient when opened from a patient context; null when opened globally. */
  patientId: string | null;
  /** Patient's open case to attach the new diagnosis to; null disables submit (create only). */
  /** Present when correcting an existing condition; null for a new diagnosis. */
  editing?: ConditionDto | null;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

function codeLabel(c: { display?: string; text?: string; code?: string }): string {
  return c.display ?? c.text ?? c.code ?? "Condition";
}

/** Dialog for adding or correcting a problem list Condition. Presentational — caller resolves the case. */
export function AddConditionDialog({ patientId, editing, open, onClose, onExited }: AddConditionDialogProps) {
  const { patients, isLoading: isLoadingPatients } = usePatients();
  // The problem-list diagnosis attaches to the patient's open episode. Opened from the
  // palette without a patient, there is no case and submit stays disabled.
  const patientCases = useCases(patientId ? { patientId } : { ids: [] });
  const activeCaseId =
    patientCases.find((c) => c.status !== "finished" && c.status !== "cancelled" && c.status !== "entered-in-error")
      ?.caseId ?? null;

  const isEditing = editing != null;
  // Patient came from the route (or the edited condition) — lock the selector.
  const patientLocked = patientId != null || isEditing;

  // `editing.code` is already the flat read-model shape the form wants, so it
  // travels straight through — no rehydration into `CodeableConcept`.
  const defaultValues: ConditionFormValues = editing
    ? {
        patientId: editing.patientId,
        code: editing.code,
        onsetDate: editing.onsetDateTime ? new Date(editing.onsetDateTime) : null,
        note: editing.notes?.[0]?.text ?? "",
      }
    : { ...DEFAULT_CONDITION_FORM_VALUES, patientId: patientId ?? "" };

  const { once } = useSubmitOnce();
  const form = useAppForm({
    defaultValues,
    validators: { onDynamic: conditionFormSchema },
    onSubmit: ({ value }) => {
      if (!editing && !activeCaseId) return;
      once((entityId) => {
        if (editing) updateConditionFromForm(value, editing.conditionId);
        else if (activeCaseId) addCaseDiagnosisFromForm(value, activeCaseId, entityId);
        onClose();
      });
    },
    onSubmitInvalid: focusFirstInvalid,
  });

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} onOpenChangeComplete={(next) => !next && onExited()}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitle>{isEditing ? "Edit Condition" : "Add Patient Condition"}</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.AppField name="patientId">
            {(field) => (
              <field.PatientField
                label="Patient"
                patients={patients}
                isLoading={isLoadingPatients}
                disabled={patientLocked}
              />
            )}
          </form.AppField>

          {isEditing && editing ? (
            <div className="flex flex-col gap-1.5">
              <Label>Condition</Label>
              <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                <span className="text-sm font-medium">{codeLabel(editing.code)}</span>
                {editing.code.code && (
                  <code className="inline-flex h-5 items-center rounded border px-1.5 font-mono text-xs text-muted-foreground">
                    {editing.code.code}
                  </code>
                )}
              </div>
            </div>
          ) : (
            <form.AppField name="code">{(field) => <field.IcdCodeField label="Condition" />}</form.AppField>
          )}

          <form.AppField name="onsetDate">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label>Onset Date</Label>
                <PropertyPicker<Date>
                  value={field.state.value ?? null}
                  onChange={(d) => field.handleChange(d)}
                  placeholder="Set onset date"
                  icon={<CalendarDays className="size-3.5" />}
                  displayValue={
                    field.state.value
                      ? field.state.value.toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : null
                  }
                >
                  <PropertyPickerCalendar selected={field.state.value ?? undefined} mode="single" />
                </PropertyPicker>
              </div>
            )}
          </form.AppField>

          <form.AppField name="note">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="condition-note">
                  Note <span className="text-muted-foreground">optional</span>
                </Label>
                <Textarea
                  id="condition-note"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Clinical narrative or context..."
                  rows={3}
                />
              </div>
            )}
          </form.AppField>

          {!isEditing && patientLocked && !activeCaseId && (
            <p className="text-sm text-destructive">Patient has no open case — start intake before adding diagnoses.</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <form.AppForm>
              <form.SubmitButton disabled={!isEditing && !activeCaseId}>
                {isEditing ? "Save" : "Add Condition"}
              </form.SubmitButton>
            </form.AppForm>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
